/**
 * Convert a shareable Loom/YouTube URL into an embeddable iframe src.
 * Mirrors backend FeatureOfTheWeek::getEmbedUrlAttribute.
 */
export const toVideoEmbedUrl = (url) => {
  const value = (url || "").trim();
  if (!value) {
    return null;
  }

  let match = value.match(/loom\.com\/share\/([a-zA-Z0-9]+)/i);
  if (match) {
    return `https://www.loom.com/embed/${match[1]}`;
  }

  match = value.match(/loom\.com\/embed\/([a-zA-Z0-9]+)/i);
  if (match) {
    return `https://www.loom.com/embed/${match[1]}`;
  }

  match = value.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/i);
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }

  match = value.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i);
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }

  return value;
};

export const FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL =
  "https://vertex360.io/video-gallery/";

/** WordPress REST API for the public Video Gallery page (same source on every environment). */
export const FEATURE_OF_THE_WEEK_VIDEO_GALLERY_WP_API =
  "https://vertex360.io/wp-json/wp/v2/pages?slug=video-gallery";

const normalizeEmbedUrl = (url) => {
  const value = (url || "").trim();
  if (!value) {
    return "";
  }

  let match = value.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
  if (match) {
    return `https://www.loom.com/embed/${match[1]}`;
  }

  match = value.match(
    /(?:youtube\.com\/embed\/|youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/i
  );
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }

  return value.split("?")[0] || value;
};

const toShareableUrl = (embedUrl) => {
  let match = embedUrl.match(/loom\.com\/embed\/([a-zA-Z0-9]+)/i);
  if (match) {
    return `https://www.loom.com/share/${match[1]}`;
  }

  match = embedUrl.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i);
  if (match) {
    return `https://www.youtube.com/watch?v=${match[1]}`;
  }

  return embedUrl;
};

/**
 * Parse videoData from the Video Gallery WordPress page HTML (mirrors backend VideoGalleryService).
 */
export const parseVideoGalleryVideosFromContent = (content) => {
  if (!content || typeof content !== "string") {
    return [];
  }

  const match = content.match(/var\s+videoData\s*=\s*(\[[\s\S]*?\]);/);
  if (!match) {
    return [];
  }

  let json = match[1]
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/^\s*\/\/.*$/gm, "")
    .replace(/,\s*([\]}])/g, "$1");

  let decoded;
  try {
    decoded = JSON.parse(json);
  } catch {
    return [];
  }

  if (!Array.isArray(decoded)) {
    return [];
  }

  const videos = [];
  const seen = new Set();

  decoded.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    let rawSrc = String(item.src ?? "").trim().replace(/["']+$/, "");
    if (!rawSrc) {
      return;
    }

    const embedUrl = normalizeEmbedUrl(rawSrc);
    const videoUrl = toShareableUrl(embedUrl);
    const key = embedUrl.toLowerCase();

    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    videos.push({
      title: String(item.title ?? "Untitled video").trim(),
      category: String(item.category ?? "General").trim(),
      duration: String(item.duration ?? "").trim() || null,
      video_url: videoUrl,
      embed_url: embedUrl,
    });
  });

  return videos;
};

export const parseVideoGalleryCategoriesFromContent = (content) => {
  if (!content || typeof content !== "string") {
    return ["All"];
  }

  const match = content.match(/var\s+categories\s*=\s*(\[[\s\S]*?\]);/);
  if (!match) {
    return ["All"];
  }

  try {
    const decoded = JSON.parse(match[1]);
    if (!Array.isArray(decoded)) {
      return ["All"];
    }

    return decoded
      .map((category) => (typeof category === "string" ? category.trim() : null))
      .filter(Boolean);
  } catch {
    return ["All"];
  }
};

export const buildVideoGalleryFromWpPages = (pages) => {
  const content = pages?.[0]?.content?.rendered ?? "";

  if (!content) {
    throw new Error("Video Gallery page content is empty");
  }

  return {
    gallery_url: FEATURE_OF_THE_WEEK_VIDEO_GALLERY_URL,
    categories: parseVideoGalleryCategoriesFromContent(content),
    videos: parseVideoGalleryVideosFromContent(content),
  };
};

export const emptyFeatureOfTheWeekForm = () => ({
  uuid: null,
  title: "Feature of the Week",
  description: "",
  video_url: "",
  status: "draft",
});

/** Normalize gallery/share/embed URLs so we can match a selected Video Gallery item. */
export const normalizeVideoIdentity = (url) => {
  const embed = toVideoEmbedUrl(url);
  if (!embed) {
    return "";
  }

  const loom = embed.match(/loom\.com\/embed\/([a-zA-Z0-9]+)/i);
  if (loom) {
    return `loom:${loom[1].toLowerCase()}`;
  }

  const youtube = embed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i);
  if (youtube) {
    return `youtube:${youtube[1]}`;
  }

  return embed.toLowerCase();
};

export const findGalleryVideoByUrl = (videos, videoUrl) => {
  const identity = normalizeVideoIdentity(videoUrl);
  if (!identity || !Array.isArray(videos)) {
    return null;
  }

  return (
    videos.find(
      (video) =>
        normalizeVideoIdentity(video.video_url) === identity ||
        normalizeVideoIdentity(video.embed_url) === identity
    ) || null
  );
};